// Traced-shape readout shared by both roof-estimate surfaces. Beyond listing each shape it does
// the one correction that makes a satellite trace usable for roofing: a polygon traced on a
// top-down image is a FLAT FOOTPRINT, so when a pitch is active every area is also shown
// pitch-corrected (footprint × pitch factor = real sloped roof area). Without that, a trace
// systematically under-reports — at 8/12 by ~20%.
import type { DrawnShape } from '@/components/QuickQuote/QuickQuoteMap';
import { formatPitch, pitchMultiplier } from '@/lib/roofPitch';

interface TracedMeasurementsProps {
  shapes: DrawnShape[];
  /** Rise per 12" applied to traced footprints, or null to report flat areas only. */
  activePitch: number | null;
  onRemove?: (id: number) => void;
  /** Hint shown when nothing has been traced yet. */
  emptyHint?: string;
}

const fmt = (n: number | null | undefined, digits = 0): string =>
  n == null ? '' : n.toLocaleString(undefined, { maximumFractionDigits: digits });

export function TracedMeasurements({ shapes, activePitch, onRemove, emptyHint }: TracedMeasurementsProps) {
  const factor = activePitch != null ? pitchMultiplier(activePitch) : 1;
  const polygons = shapes.filter((s) => s.type === 'polygon');
  const footprintTotal = polygons.reduce((sum, s) => sum + (s.area_sqft ?? 0), 0);
  const slopedTotal = footprintTotal * factor;

  if (shapes.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-ui-border px-3 py-2 text-xs text-ui-text3">
        {emptyHint ?? 'Use the drawing tools on the map to trace the roof and measure area.'}
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      {shapes.map((s) => (
        <div key={s.id} className="rounded-lg border border-ui-border bg-ui-subtle px-2.5 py-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="min-w-0 truncate text-xs text-ui-text">
              <span className="font-medium">{s.label}</span>
              {s.type === 'polygon' ? (
                <span className="text-ui-text2">
                  {activePitch != null ? (
                    <>
                      {' '}— <span className="font-semibold text-ui-text">
                        {fmt((s.area_sqft ?? 0) * factor)} sqft
                      </span>{' '}
                      ({fmt(((s.area_sqft ?? 0) * factor) / 100, 2)} squares) @ {formatPitch(activePitch)}
                    </>
                  ) : (
                    <>
                      {' '}— {fmt(s.area_sqft)} sqft ({fmt(s.squares, 2)} squares)
                    </>
                  )}
                </span>
              ) : (
                <span className="text-ui-text2"> — {fmt(s.length_ft)} ft</span>
              )}
            </span>
            {onRemove && (
              <button
                onClick={() => onRemove(s.id)}
                aria-label={`Remove ${s.label}`}
                className="shrink-0 text-ui-text3 hover:text-ui-text"
              >
                ×
              </button>
            )}
          </div>
          {/* Footprint kept visible alongside the corrected figure — the correction is an
              assumption about pitch, so the raw measurement shouldn't disappear behind it. */}
          {s.type === 'polygon' && activePitch != null && (
            <p className="mt-0.5 text-xs text-ui-text3">
              {fmt(s.area_sqft)} sqft flat footprint × {pitchMultiplier(activePitch).toFixed(3)}
            </p>
          )}
          {s.type === 'polygon' && s.edge_lengths_ft && s.edge_lengths_ft.length > 0 && (
            <p className="mt-1 text-xs text-ui-text3">
              Sides: {s.edge_lengths_ft.map((len, i) => `${i + 1}. ${fmt(len, 1)} ft`).join('  ·  ')}
            </p>
          )}
        </div>
      ))}

      {polygons.length >= 2 && (
        <div className="flex items-center justify-between rounded-lg border border-ui-border px-2.5 py-1.5">
          <span className="text-xs font-semibold text-ui-text">Total traced area</span>
          <span className="text-xs font-semibold text-ui-text">
            {fmt(slopedTotal)} sqft · {fmt(slopedTotal / 100, 2)} squares
            {activePitch != null && (
              <span className="font-normal text-ui-text3"> @ {formatPitch(activePitch)}</span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

export default TracedMeasurements;
